import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon-red.svg';
import { ReactComponent as CCS2Icon } from '../../assets/icons/device-management/ccs2.svg';
import { ReactComponent as RestartIcon } from '../../assets/icons/device-management/restart.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding } from '../../config/branding';
import { ocppMessageType } from '../../config/enum';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { ChargingPointProperties } from '../../interface/charging-point.interface';
import { DeviceListingProperties } from '../../interface/device-management.interface';
import { useAppDispatch } from '../../redux/hooks';
import { fetchAllDevices } from '../../redux/slices/DeviceManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchAllDevices as fetchPersonalDetails } from '../../redux/slices/PersonalDeviceSlice';
import { updateSidePanelData } from '../../redux/slices/TableSidePanelSlice';
import device from '../../utils/api/device';
import ocppEvents from '../../utils/api/ocpp-events';
import DeleteWarning from '../delete-warning/DeleteWarning';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

import EndConsumerSidePanel from './EndConsumerSidePanel';
import FacilitySidePanel from './FacilitySidePanel';
import StationSidePanel from './StationSidePanel';

interface DeviceSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  deviceModule?: boolean;
}

interface ModalBoxDetail {
  title: String;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

interface ClearCacheProperties {
  devices: string[];
  resetType: string;
}
/**
 * check the lat and lng is valid or not
 * @param position
 * @returns
 */
const validatePosition = (position: { latitude: any; longitude: any }) => {
  const lat = parseFloat(position.latitude);
  const lng = parseFloat(position.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Device side panel
 * @returns
 */
export default function DeviceSidePanel(properties: DeviceSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const branding = useAppBranding();
  const { fetchIdFrom, id, deviceModule } = properties;

  const { isLoaded } = useGoogleMaps();
  const dispatch = useAppDispatch();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });

  const [configuration, setConfiguration] = useState<ClearCacheProperties>({
    devices: [],
    resetType: '',
  });

  const mapReference = useRef<google.maps.Map | null>(null);

  const [deviceDetails, setDeviceDetails] = useState<ChargingPointProperties>({
    connectors: [],
    isPersonalCharger: false,
  });

  const [overlaySidePanel, setOverlaySidePanel] = useState<
    'closed' | 'station' | 'facility' | 'end-consumer'
  >('closed');

  const deviceGeneralDetails = [
    {
      id: 1,
      label: t('stationName'),
      key: 'chargingStationName',
      disableField: 'personalCharger',
      /**
       * Open side panel for station view
       * @returns
       */
      handleClick: () => setOverlaySidePanel('station'),
    },
    {
      id: 2,
      label: t('userName'),
      key: 'userName',
      disableField: 'commercialCharger',
      /**
       * Open side panel for station view
       * @returns
       */
      handleClick: () => setOverlaySidePanel('end-consumer'),
      /**
       * Custom data to display for user name
       * @param rowData
       * @returns
       */
      customData: (rowData: ChargingPointProperties) => (
        <p
          role="presentation"
          className="hyperlink"
          onClick={() =>
            setOverlaySidePanel('end-consumer')
          }>{`${rowData.userFirstName} ${rowData.userLastName}`}</p>
      ),
    },
    {
      id: 3,
      label: t('deviceId'),
      key: 'chargingPointSn',
      disableField: 'never',
    },
    {
      id: 4,
      label: t('accountName'),
      key: 'accountName',
      disableField: 'personalCharger',
      /**
       * Open side panel for station view
       * @returns
       */
      handleClick: () => setOverlaySidePanel('facility'),
    },
    {
      id: 5,
      label: t('deviceModel'),
      key: 'chargePointModel',
      disableField: 'never',
    },
    {
      id: 6,
      label: t('deviceName'),
      key: 'chargingPointName',
      disableField: 'never',
    },
    {
      id: 7,
      label: t('deviceSerialNo'),
      key: 'chargingPointSn',
      disableField: 'never',
    },
    {
      id: 8,
      label: t('chargingPointVendor'),
      key: 'chargePointVendor',
      disableField: 'never',
    },
    {
      id: 9,
      label: t('firmwareVersion'),
      key: 'firmwareVersion',
      disableField: 'never',
    },
  ];
  /**
   * Close the side panel
   */
  const closeSidePanel = () => {
    searchParameters.delete('deviceId');
    setSearchParameters(searchParameters);
  };
  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = async (stationId: string) => {
    device
      .getChargingPointById(stationId)
      .then((response) => {
        setDeviceDetails({
          id: response?.data?._id || '',
          ...response?.data,
        });
        // Update the side panel data in the store
        dispatch(
          updateSidePanelData({
            type: 'deviceId',
            value: {
              id: response?.data?._id || '',
              ...response?.data,
            },
          }),
        );
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('somethingWentWrong'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };
  /**
   * Function to reset configuration input
   */
  const handleResetInput = () => {
    setModalBoxStatus('close');
    setConfiguration({
      devices: [],
      resetType: '',
    });
  };
  /**
   * reset command
   */
  const handleSubmit = async () => {
    ocppEvents
      .anyCommand({
        type: ocppMessageType.RESET_CP,
        body: {
          resetType: configuration.resetType,
        },
        chargingPointSns: configuration.devices,
      })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('resetDeviceRequested')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleResetInput}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('somethingWentWrong'),
            type: 'error',
            code: 0,
          }),
        ),
      );
  };
  /**
   * delete action
   * @param chargingPoint
   */
  const handleDeleteDevice = (chargingPoint: DeviceListingProperties) => {
    device
      .deleteChargingPoint(chargingPoint.id)
      .then(() => {
        closeSidePanel();
        if (deviceDetails?.isPersonalCharger) {
          const data = {
            page: 1,
            limit: 20,
            deviceType: 'personal',
          };
          dispatch(fetchPersonalDetails(data));
        } else {
          const data = {
            page: 1,
            limit: 20,
            deviceType: 'personal',
          };
          dispatch(fetchAllDevices(data));
        }
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('deviceDeletedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleResetInput}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('somethingWentWrong'),
            type: 'error',
            code: 0,
          }),
        ),
      );
  };
  /**
   * Charger Delete
   * @param chargingPoint
   * @param event
   */
  const handleDeleteDeviceWarning = (chargingPoint: any, event: React.MouseEvent) => {
    event.stopPropagation();

    setModalBoxStatus('open');

    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <DeleteWarning
          selectedRowId={chargingPoint._id}
          title={t('deleteDeviceConfirm', { name: chargingPoint.chargingPointName })}
          description={t('deleteDeviceDescription')}
          warningLabel={`${t('warning')}:`}
          cancelButtonLabel={t('cancel')}
          submitButtonLabel={t('delete')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(ids) => ids && handleDeleteDevice(chargingPoint)}
        />
      ),
      notificationModal: true,
    });
  };

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('deviceId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
  }, [searchParameters, id]);

  return (
    <div className="device-side-panel-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="device-side-panel-header">
        {deviceModule && (
          <>
            <p>{t('chargerDetail')}</p>
            <div className="action-button">
              <RestartIcon onClick={handleSubmit} />
              <DeleteIcon onClick={(event) => handleDeleteDeviceWarning(deviceDetails, event)} />
            </div>
          </>
        )}
      </div>
      <div className="device-side-panel-container">
        <div className="device-preview">
          <div className="device-image-status-wrap">
            <div className="device-status-text-wrap">
              {deviceDetails.isDeviceOffline !== undefined && (
                <div
                  className={`device-status-text-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
                  <div className="icon" />
                  <p>{deviceDetails.isDeviceOffline ? t('offline') : t('online')}</p>
                </div>
              )}
            </div>
            <img
              src={branding.images.emptyChargerImage}
              alt={t('emptyCharger')}
              className="device-details-image"
            />

            {deviceDetails.isDeviceOffline !== undefined && (
              <div
                className={`device-status-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
                <div className="static-container" />
                <div className="animation-container" />
              </div>
            )}
          </div>
          <div className="map-address-container">
            <div className="device-side-panel-map">
              <h6>{t('deviceLocation')}</h6>
              <div className="map-container">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{
                      width: '100%',
                      height: '100%',
                    }}
                    center={{
                      lat: Number(deviceDetails.latitude),
                      lng: Number(deviceDetails.longitude),
                    }}
                    zoom={3}
                    onLoad={(map) => {
                      mapReference.current = map; // Assign the map instance to the reference
                    }}>
                    {deviceDetails &&
                      (() => {
                        const validPosition = validatePosition({
                          latitude: deviceDetails.latitude,
                          longitude: deviceDetails.longitude,
                        });
                        return validPosition ? (
                          <Marker
                            key="01"
                            position={validPosition}
                            // clusterer={clusterer}
                            icon={{
                              url: branding.icons.stationLocator,
                              scaledSize: new window.google.maps.Size(44, 44), // Width, Height
                            }}
                          />
                        ) : null;
                      })()}
                  </GoogleMap>
                ) : null}
              </div>
            </div>
            <div className="address-wrap">
              <h6>{t('address')}</h6>
              <p>{deviceDetails?.address}</p>
            </div>
          </div>
        </div>
        <div className="content-wrap-with-scroll">
          {deviceDetails?.connectors && deviceDetails.connectors.length ? (
            <div className="connector-wrap">
              {deviceDetails.connectors.map((connector: any, index: number) => (
                <div className="each-connector">
                  <div className="connector-type">
                    {/* To-do -> Need to configure icon based on connector type */}
                    <span>
                      <CCS2Icon style={{ color: 'var(--primary-color)' }} />
                    </span>
                    <h6>{`(${deviceDetails.connectorType})`}</h6>
                  </div>
                  <p>
                    {t('connector')} {connector.connectorId}
                  </p>
                  <div className={`connector-status ${connector.status}`}>
                    {t(connector.status.toLowerCase())}
                  </div>
                  {deviceDetails?.connectors && index !== deviceDetails.connectors.length - 1 && (
                    <div className="right-border" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <h6>{t('connectorDataUnavailable')}</h6>
          )}
        </div>
        <div className="device-side-panel-details">
          {deviceGeneralDetails
            ?.filter(
              (generalDetails) =>
                generalDetails.disableField === 'never' ||
                (generalDetails.disableField === 'personalCharger' &&
                  !deviceDetails.isPersonalCharger) ||
                (generalDetails.disableField === 'commercialCharger' &&
                  deviceDetails.isPersonalCharger),
            )
            ?.map((generalDetail) => (
              <div className="each-device-data">
                <h6>{generalDetail.label}</h6>
                {generalDetail.customData ? (
                  generalDetail.customData(deviceDetails)
                ) : (
                  <p
                    role="presentation"
                    onClick={generalDetail.handleClick ? generalDetail.handleClick : () => {}}
                    className={generalDetail.handleClick ? 'hyperlink' : ''}>
                    {String(
                      deviceDetails[generalDetail.key as keyof ChargingPointProperties] || t('na'),
                    )}
                  </p>
                )}
              </div>
            ))}
        </div>
      </div>
      <div className="device-side-panel-description" />
      <div
        className={`device-panel-station-view-wrap ${overlaySidePanel !== 'closed' ? 'active' : ''}`}>
        <div className="side-panel-header">
          {/* Need to configure after new tab integration */}
          <span style={{ opacity: '0.5', cursor: 'not-allowed' }}>{/* <NewTabIcon /> */}</span>
          <span role="presentation" onClick={() => setOverlaySidePanel('closed')}>
            <CloseIcon />
          </span>
        </div>
        {(() => {
          let overlaySidePanelToShow;
          if (overlaySidePanel === 'station') {
            overlaySidePanelToShow = (
              <div className="side-panel-content">
                <StationSidePanel fetchIdFrom="props" id={deviceDetails.chargingStation} />
              </div>
            );
          } else if (overlaySidePanel === 'facility') {
            overlaySidePanelToShow = (
              <div className="side-panel-content">
                <FacilitySidePanel fetchIdFrom="props" id={deviceDetails.accountCustomId} />
              </div>
            );
          } else if (overlaySidePanel === 'end-consumer') {
            overlaySidePanelToShow = (
              <div className="side-panel-content">
                <EndConsumerSidePanel fetchIdFrom="props" id={deviceDetails.userCustomId} />
              </div>
            );
          }
          return overlaySidePanelToShow;
        })()}
      </div>
    </div>
  );
}
